import { AlertCircle, Shield, ShieldOff } from 'lucide-react';

import { useSelector } from 'react-redux';
import { Card, Spinner } from '@design-system';
import { ContactSupportButton } from '@/components/ContactSupportButton';
import { LicensedAgentCta } from '@/features/guided-financial-planning/components/licensed-agent-cta';
import { NON_WAG_NO_QUOTES_CTA, WAG_NO_QUOTES_CTA } from '@/shared/lib/constants';
import { selectClient, selectFeatureFlags, selectSession } from '@client/model/selectors';
import { useActivePilotProgram } from '@/shared/hooks/use-active-pilot-program';
import { SessionType } from '@shared/SessionType';
import { zipCodeToState } from '@shared';

export function QuotesLoadingState({
  title = 'Loading quotes based on your preferences...',
}: {
  title?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16">
      <Spinner className="size-8" />
      <p className="text-gray-500">{title}</p>
    </div>
  );
}

export function QuotesErrorState() {
  return (
    <Card className="mx-auto max-w-md px-6 py-5 text-center">
      <div className="flex flex-col items-center gap-2">
        <AlertCircle className="size-6 text-gray-300" />
        <h3 className="text-base font-medium text-gray-900">Unable to load quotes</h3>
        <p className="text-sm text-gray-500">
          We encountered an issue while loading your quotes. Please try again or contact support if
          the problem persists.
        </p>
        <ContactSupportButton
          subject="Issue loading quotes in guided planning"
          className="mt-1"
        />
      </div>
    </Card>
  );
}

/**
 * Shown when the advisor's program withholds Quote & Apply.
 *
 * Reuses the program's own `blockedPages` copy so this says exactly what Shop Insurance says,
 * rather than leaving a deliberate restriction looking like a loading failure.
 */
export function QuotesRestrictedState() {
  const program = useActivePilotProgram();
  const copy = program?.blockedPages?.['quote-apply'];

  if (!copy) {
    return null;
  }

  return (
    <div className="mx-auto max-w-lg py-12 text-center">
      <div className="bg-muted mx-auto mb-6 flex size-16 items-center justify-center rounded-full">
        <ShieldOff className="text-foreground size-8" />
      </div>
      <h3 className="text-balance text-lg font-medium text-gray-900">{copy.heading}</h3>
      <p className="text-foreground mt-2 text-balance">{copy.body}</p>
    </div>
  );
}

interface QuotesEmptyStateProps {
  reason?: GuidedPlanningNoQuotesReason;
  filterPanel?: React.ReactNode;
}

const FILTER_RELATED_REASONS: (GuidedPlanningNoQuotesReason | 'default')[] = [
  'no_policy_type_match',
  'no_preference_match',
  'default',
];

const EMPTY_STATE_CONTENT: Record<
  GuidedPlanningNoQuotesReason | 'default',
  { title: string; description: string }
> = {
  state_not_supported: {
    title: 'No quotes in our system for your state yet',
    description:
      "We don't have quotes for your state in our system yet, but that doesn't mean coverage isn't available. We're adding more states all the time — check back soon.",
  },
  age_out_of_range: {
    title: 'Insurance quotes aren’t available for your age',
    description:
      'Our system doesn’t has policies for your age, so we can’t show quotes to compare or purchase. You can still continue with a self-funding plan, we’ll model your future care costs against your own assets and income so you have a clear picture either way.',
  },
  demographics_incomplete: {
    title: 'No quotes in our system for your profile',
    description:
      "We weren't able to find quotes for your current profile in our system, but we're adding more all the time.",
  },
  no_demographics_match: {
    title: 'No quotes in our system for your profile yet',
    description:
      "We don't have quotes matching your profile in our system yet. We're constantly adding new carriers and products — check back soon.",
  },
  no_policy_type_match: {
    title: 'No quotes match your selections',
    description: 'Try broadening your selection to include more policy types.',
  },
  no_carrier_match: {
    title: 'Contact your advisor for assistance.',
    description: 'Qualifying policies are not showing based on advisor carrier settings.',
  },
  no_preference_match: {
    title: 'No quotes match your preferences',
    description: 'Try adjusting your payment or benefit structure preferences to see more options.',
  },
  default: {
    title: 'No quotes in our system for your criteria',
    description:
      "We don't have quotes matching your criteria yet, but we're adding more all the time. Try broadening your policy type selection or check back later.",
  },
};

// TEMPORARY: New York has no quotes loaded yet, so NY clients get a "coming soon"
// message instead of the generic empty state. Remove this constant and every
// isNewYorkComingSoon reference in this file once NY quotes are added.
const NEW_YORK_COMING_SOON_CONTENT = {
  title: 'Quotes for New York are coming soon',
  description:
    "We're working on bringing quotes to New York and they'll be available in our system soon. Check back shortly to compare options.",
};

export function QuotesEmptyState({ reason, filterPanel }: QuotesEmptyStateProps) {
  const { sessionType } = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const client = useSelector(selectClient);
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;

  // TEMPORARY: remove once NY quotes are added.
  const isNewYorkComingSoon =
    zipCodeToState(client?.intakeSurvey?.clientZipCode).stateAbbreviation === 'NY' &&
    reason !== 'age_out_of_range';

  const content = isNewYorkComingSoon
    ? NEW_YORK_COMING_SOON_CONTENT
    : reason
      ? EMPTY_STATE_CONTENT[reason]
      : EMPTY_STATE_CONTENT.default;
  const showFilterPanel =
    !isNewYorkComingSoon && filterPanel && (!reason || FILTER_RELATED_REASONS.includes(reason));
  const showCta =
    isNewYorkComingSoon ||
    ((!reason || !FILTER_RELATED_REASONS.includes(reason)) && reason !== 'age_out_of_range');

  const ctaText = isD2C ? WAG_NO_QUOTES_CTA : NON_WAG_NO_QUOTES_CTA;

  return (
    <div className="mx-auto max-w-lg py-12 text-center">
      <Shield className="mx-auto mb-4 size-12 text-gray-300" />
      <h3 className="text-lg font-medium text-gray-900">{content.title}</h3>
      <p className="mt-2 text-gray-500">
        {content.description}
        {showCta && <> {ctaText}</>}
      </p>
      {isD2C && <LicensedAgentCta className="mt-6" />}
      {showFilterPanel && <div className="mt-4 flex justify-center">{filterPanel}</div>}
    </div>
  );
}
